// The editor for one custom connector: definition, script, dry run, outputs
// and run history in a single wide modal.
//
// The outputs table is deliberately shaped like a Modbus device's points — an
// operator who has configured one already knows how to configure this: a key,
// where it lands, its type, and whether the historian keeps it.

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import {
  createCollector,
  deleteCollector,
  listCollectorRuns,
  runCollectorNow,
  updateCollector,
} from "../../../api";
import { registerCollectorEditor } from "../../../ai-integration/collectorEditorBridge";
import sparksIcon from "../../../assets/sparks.svg";
import { GatewayConfirmDialog, GatewayModal } from "../../../components/GatewayModal";
import NumberInput from "../../../components/NumberInput";
import Select from "../../../components/Select";
import TagPicker from "../../../components/TagPicker";
import { useNotify } from "../../../ToastContext";
import {
  MIN_POLL_INTERVAL_MS,
  MAX_TIMEOUT_MS,
  MIN_TIMEOUT_MS,
  OUTPUT_TYPES,
  RUN_HISTORY_LIMIT,
  applyOutputSuggestions,
  buildCollectorPayload,
  collectorFormFromRecord,
  formatClockTime,
  formatDuration,
  formatValue,
  inferOutputType,
  newOutputForm,
  parseParams,
  serverErrorField,
  suggestTagPath,
  validateCollectorForm,
} from "./collectors-model";

const INDENT = "    ";
const STDERR_PREVIEW_CHARS = 240;

const errorMessage = (error, fallback) => String(error?.message || error || "").trim() || fallback;

function Field({ label, hint, error, children }) {
  return (
    <div className="gwField">
      <label className="gwLabel">{label}</label>
      {children}
      {error ? <div className="gwCcFieldError">{error}</div> : null}
      {!error && hint ? <div className="gwHint">{hint}</div> : null}
    </div>
  );
}

// A textarea with a line gutter and a four-space Tab. Python is
// whitespace-significant and a browser's default Tab moves focus out of the
// box, which makes the plain control unusable for the one language this
// section accepts. No editor dependency: the gateway does not ship one.
function ScriptEditor({ value, onChange, disabled }) {
  const textareaRef = useRef(null);
  const gutterRef = useRef(null);
  const caretRef = useRef(null);
  const lineCount = value.split("\n").length;

  useLayoutEffect(() => {
    if (caretRef.current === null) return;
    const element = textareaRef.current;
    if (element) element.setSelectionRange(caretRef.current, caretRef.current);
    caretRef.current = null;
  });

  const handleKeyDown = (event) => {
    if (event.key !== "Tab") return;
    event.preventDefault();
    const element = event.target;
    const { selectionStart: start, selectionEnd: end } = element;
    const text = element.value;
    if (event.shiftKey) {
      const lineStart = text.lastIndexOf("\n", start - 1) + 1;
      const removable = text.slice(lineStart, lineStart + INDENT.length).match(/^ {1,4}/);
      if (!removable) return;
      const removed = removable[0].length;
      caretRef.current = Math.max(lineStart, start - removed);
      onChange(text.slice(0, lineStart) + text.slice(lineStart + removed));
      return;
    }
    caretRef.current = start + INDENT.length;
    onChange(text.slice(0, start) + INDENT + text.slice(end));
  };

  return (
    <div className="gwCcScript">
      <pre className="gwCcScriptGutter" ref={gutterRef} aria-hidden="true">
        {Array.from({ length: lineCount }, (_, index) => index + 1).join("\n")}
      </pre>
      <textarea
        ref={textareaRef}
        className="gwCcScriptArea"
        aria-label="Collector script"
        spellCheck={false}
        wrap="off"
        disabled={disabled}
        value={value}
        onKeyDown={handleKeyDown}
        onChange={(event) => onChange(event.target.value)}
        onScroll={(event) => {
          if (gutterRef.current) gutterRef.current.scrollTop = event.target.scrollTop;
        }}
      />
    </div>
  );
}

function EnvKeysField({ value, onChange, error, disabled }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const key = draft.trim();
    if (!key || value.includes(key)) {
      setDraft("");
      return;
    }
    onChange([...value, key]);
    setDraft("");
  };
  return (
    <Field
      label="Env keys"
      error={error}
      hint="Nombra acá las variables de entorno que el script lee. Es una solicitud, no un permiso: solo llegan si COLLECTORS_ENV_ALLOWLIST del despliegue también las lista. Nunca guardes un secreto en los params."
    >
      <div className="gwCcChips">
        {value.map((key) => (
          <span className="gwCcChip" key={key}>
            {key}
            <button
              type="button"
              className="gwCcChipRemove"
              aria-label={`Remove ${key}`}
              disabled={disabled}
              onClick={() => onChange(value.filter((current) => current !== key))}
            >
              ×
            </button>
          </span>
        ))}
        <input
          className="gwInput gwCcChipInput"
          aria-label="Add env key"
          placeholder="CMG_API_TOKEN"
          value={draft}
          disabled={disabled}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={add}
          onKeyDown={(event) => {
            if (event.key !== "Enter") return;
            event.preventDefault();
            add();
          }}
        />
      </div>
    </Field>
  );
}

function OutputsTable({
  outputs,
  errors,
  disabled,
  onChange,
  onRemove,
  onAccept,
  onAdd,
  valueKeys,
}) {
  const pending = outputs.filter((output) => output.suggested).length;
  return (
    <div className="gwCardSection">
      <div className="gwSectionTitle gwCcSectionHead">
        <span>Outputs</span>
        <button type="button" className="gwSecondaryBtn" disabled={disabled} onClick={onAdd}>
          Add output
        </button>
      </div>
      <div className="gwCardSectionBody">
        {pending > 0 ? (
          <div className="gwCcWarning" role="status">
            La IA sugirió {pending} output{pending === 1 ? "" : "s"}. Revisa la key, el tag path y
            el tipo, y acéptalos: mientras sigan como sugerencia no se guardan con el colector.
          </div>
        ) : null}
        {outputs.length === 0 ? (
          <div className="gwCcEmpty">
            Todavía no hay outputs. Corre el script con Run now y agrega los values que
            quieras dejar en tags, o agrega una fila a mano.
          </div>
        ) : (
          <table className="gwCcOutputs">
            <thead>
              <tr>
                <th scope="col">Key</th>
                <th scope="col">Tag path</th>
                <th scope="col">Type</th>
                <th scope="col">Historian</th>
                <th scope="col">Interval (s)</th>
                <th scope="col" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {outputs.map((output) => {
                const rowError = errors?.[output.uid] || {};
                return (
                  <tr key={output.uid} className={output.suggested ? "gwCcSuggestedRow" : undefined}>
                    <td>
                      <input
                        className="gwInput"
                        aria-label={`Output key ${output.key || ""}`}
                        list="gwCcValueKeys"
                        value={output.key}
                        disabled={disabled}
                        onChange={(event) => onChange(output.uid, { key: event.target.value })}
                      />
                      {output.suggested ? (
                        <div className="gwCcSuggestedBadge">Sugerido por IA</div>
                      ) : null}
                      {rowError.key ? <div className="gwCcFieldError">{rowError.key}</div> : null}
                    </td>
                    <td>
                      <div className="gwCcTagCell">
                        <input
                          className="gwInput"
                          aria-label={`Tag path for ${output.key || "output"}`}
                          placeholder="collectors/cmg/cmg_usd_mwh"
                          value={output.tagPath}
                          disabled={disabled}
                          onChange={(event) => onChange(output.uid, { tagPath: event.target.value })}
                        />
                        {/* The picker never holds a selection: a collector's
                            tag usually does not exist yet, so browsing is a way
                            to fill the text field, not to replace it. */}
                        <TagPicker
                          value=""
                          displayValue=""
                          placeholder="Browse…"
                          disabled={disabled}
                          onChange={(_id, fullPath) => {
                            if (fullPath) onChange(output.uid, { tagPath: fullPath });
                          }}
                        />
                      </div>
                      {rowError.tagPath ? (
                        <div className="gwCcFieldError">{rowError.tagPath}</div>
                      ) : null}
                    </td>
                    <td>
                      <Select
                        value={output.type}
                        options={OUTPUT_TYPES}
                        disabled={disabled}
                        onChange={(type) => onChange(output.uid, { type })}
                      />
                    </td>
                    <td className="gwCcHistorianCell">
                      <button
                        type="button"
                        className={`gwToggle ${output.historianEnabled ? "isOn" : ""}`}
                        role="switch"
                        aria-checked={output.historianEnabled}
                        aria-label={`Historian for ${output.key || "output"}`}
                        disabled={disabled}
                        onClick={() =>
                          onChange(output.uid, { historianEnabled: !output.historianEnabled })
                        }
                      >
                        <span />
                      </button>
                    </td>
                    <td>
                      <NumberInput
                        value={output.historianIntervalSeconds}
                        min={0}
                        aria-label={`Historian interval for ${output.key || "output"}`}
                        disabled={disabled || !output.historianEnabled}
                        onChange={(event) =>
                          onChange(output.uid, { historianIntervalSeconds: event.target.value })
                        }
                      />
                    </td>
                    <td>
                      {output.suggested ? (
                        <div className="gwCcSuggestedActions">
                          <button
                            type="button"
                            className="gwSecondaryBtn"
                            aria-label={`Accept suggested output ${output.key || ""}`}
                            disabled={disabled}
                            onClick={() => onAccept(output.uid)}
                          >
                            Accept
                          </button>
                          <button
                            type="button"
                            className="gwGhostBtn"
                            aria-label={`Discard suggested output ${output.key || ""}`}
                            disabled={disabled}
                            onClick={() => onRemove(output.uid)}
                          >
                            Discard
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="gwGhostBtn"
                          aria-label={`Remove output ${output.key || ""}`}
                          disabled={disabled}
                          onClick={() => onRemove(output.uid)}
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <datalist id="gwCcValueKeys">
          {valueKeys.map((key) => (
            <option key={key} value={key} />
          ))}
        </datalist>
        <div className="gwHint">
          El historian nunca se apaga desde acá: si el tag ya venía grabando, sigue
          grabando. Un intervalo mayor que cero graba por cambio y además cada ese lapso.
        </div>
      </div>
    </div>
  );
}

function DryRunPanel({ result, error, running, dirty, saved, onRun, onAddOutput, mappedKeys }) {
  const [showStdout, setShowStdout] = useState(false);
  const values = result?.values && typeof result.values === "object" ? result.values : {};
  const valueKeys = Object.keys(values);
  return (
    <div className="gwCardSection">
      <div className="gwSectionTitle gwCcSectionHead">
        <span>Run now</span>
        <button
          type="button"
          className="gwSecondaryBtn"
          disabled={!saved || running}
          onClick={onRun}
        >
          {running ? "Running…" : "Run now (dry run)"}
        </button>
      </div>
      <div className="gwCardSectionBody">
        <div className="gwHint">
          {saved
            ? "Un dry run corre el script guardado y convierte cada value, pero no escribe ningún tag ni queda en el historial."
            : "Guarda el colector para poder probarlo: Run now corre el script tal como quedó en el servidor."}
        </div>
        {saved && dirty ? (
          <div className="gwCcWarning" role="status">
            Hay cambios sin guardar. Run now ejecuta la última versión guardada.
          </div>
        ) : null}
        {error ? (
          <div className="gwAlert" role="alert">
            <div className="gwAlertTitle">No se pudo correr el colector</div>
            <div className="gwAlertBody">{error}</div>
          </div>
        ) : null}
        {result ? (
          <div className="gwCcRunResult">
            <div className="gwCcRunMeta">
              <span className={result.error ? "gwCcDot is-failing" : "gwCcDot is-ok"} />
              <span>{result.error ? "Error" : "OK"}</span>
              <span>{formatDuration(result.duration_ms)}</span>
              <span>{valueKeys.length} values</span>
            </div>
            {result.error ? <div className="gwCcRunError">{result.error}</div> : null}
            {valueKeys.length > 0 ? (
              <table className="gwCcValues">
                <thead>
                  <tr>
                    <th scope="col">Key</th>
                    <th scope="col">Value</th>
                    <th scope="col" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {valueKeys.map((key) => (
                    <tr key={key}>
                      <td><code>{key}</code></td>
                      <td className="gwCcValueCell">{formatValue(values[key])}</td>
                      <td>
                        <button
                          type="button"
                          className="gwGhostBtn"
                          disabled={mappedKeys.includes(key)}
                          onClick={() => onAddOutput(key, values[key])}
                        >
                          {mappedKeys.includes(key) ? "Mapped" : "Add output"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="gwCcEmpty">El script no devolvió ningún value.</div>
            )}
            <div className="gwCcStreams">
              <div className="gwCcStream">
                <div className="gwCcStreamHead">
                  <span>stderr</span>
                </div>
                <pre className="gwCcPre">{result.stderr || "(vacío)"}</pre>
              </div>
              <div className="gwCcStream">
                <div className="gwCcStreamHead">
                  <span>stdout</span>
                  <button
                    type="button"
                    className="gwGhostBtn"
                    onClick={() => setShowStdout((current) => !current)}
                  >
                    {showStdout ? "Hide" : "Show"}
                  </button>
                </div>
                {showStdout ? <pre className="gwCcPre">{result.stdout || "(vacío)"}</pre> : null}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function RunRow({ run }) {
  const [expanded, setExpanded] = useState(false);
  const stderr = String(run.stderr || "");
  const truncated = stderr.length > STDERR_PREVIEW_CHARS;
  return (
    <div className="gwCcRun">
      <div className="gwCcRunLine">
        <span className={run.ok ? "gwCcDot is-ok" : "gwCcDot is-failing"} aria-hidden="true" />
        <span className="gwCcRunTime">{formatClockTime(run.started_at)}</span>
        <span>{run.ok ? "OK" : "Error"}</span>
        <span>{formatDuration(run.duration_ms)}</span>
        <span>{run.written_tags} tags</span>
        {run.error ? <span className="gwCcRunError">{run.error}</span> : null}
        {stderr ? (
          <button
            type="button"
            className="gwGhostBtn"
            aria-expanded={expanded}
            onClick={() => setExpanded((current) => !current)}
          >
            {expanded ? "Hide stderr" : "Show stderr"}
          </button>
        ) : null}
      </div>
      {stderr ? (
        <pre className="gwCcPre gwCcRunStderr">
          {expanded || !truncated ? stderr : `${stderr.slice(0, STDERR_PREVIEW_CHARS)}…`}
        </pre>
      ) : null}
    </div>
  );
}

function RunHistory({ collectorId }) {
  const [open, setOpen] = useState(false);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const list = await listCollectorRuns(collectorId, { limit: RUN_HISTORY_LIMIT });
      setRuns(Array.isArray(list) ? list : []);
    } catch (loadError) {
      setError(errorMessage(loadError, "No se pudo leer el historial de corridas."));
    } finally {
      setLoading(false);
    }
  }, [collectorId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  return (
    <div className="gwCardSection">
      <div className="gwSectionTitle gwCcSectionHead">
        <span>Run history</span>
        <button
          type="button"
          className="gwSecondaryBtn"
          aria-expanded={open}
          onClick={() => setOpen((current) => !current)}
        >
          {open ? "Hide runs" : "Show runs"}
        </button>
      </div>
      {open ? (
        <div className="gwCardSectionBody gwCcRuns">
          {loading ? <div className="gwCcEmpty">Cargando corridas…</div> : null}
          {!loading && error ? (
            <div className="gwAlert" role="alert">
              <div className="gwAlertTitle">Historial no disponible</div>
              <div className="gwAlertBody">{error}</div>
            </div>
          ) : null}
          {!loading && !error && runs.length === 0 ? (
            <div className="gwCcEmpty">Este colector todavía no ha corrido.</div>
          ) : null}
          {!loading && !error
            ? runs.map((run) => <RunRow key={run.id} run={run} />)
            : null}
        </div>
      ) : null}
    </div>
  );
}

export function CustomCollectorEditor({ collector, canConfigure, onClose, onSaved, onDeleted }) {
  const notify = useNotify();
  const [record, setRecord] = useState(collector || null);
  const [form, setForm] = useState(() => collectorFormFromRecord(collector));
  const [errors, setErrors] = useState({});
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [runResult, setRunResult] = useState(null);
  const [runError, setRunError] = useState("");
  const [running, setRunning] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const saved = Boolean(record?.id);
  const locked = saving || deleting || !canConfigure;

  // What the assistant reads and writes through. Kept in a ref so the bridge
  // handle registers once for the modal's lifetime while still seeing the
  // current script — including whatever the operator typed after the drawer
  // opened.
  const liveRef = useRef(null);
  liveRef.current = { form, record, locked, canConfigure };

  const readForAssistant = useCallback(() => {
    const live = liveRef.current;
    if (!live) return null;
    const params = parseParams(live.form.params);
    return {
      name: live.form.name,
      description: live.form.description,
      saved: Boolean(live.record?.id),
      editable: !live.locked,
      poll_interval_seconds: Number(live.form.pollSeconds),
      timeout_seconds: Number(live.form.timeoutSeconds),
      params: params.ok ? params.value : undefined,
      params_error: params.ok ? undefined : params.error,
      env_keys: [...live.form.envKeys],
      outputs: live.form.outputs.map((output) => ({
        key: output.key,
        tag_path: output.tagPath,
        type: output.type,
        suggested: Boolean(output.suggested),
      })),
      script: live.form.script,
      script_lines: live.form.script.split("\n").length,
    };
  }, []);

  const applyFromAssistant = useCallback(({ script, outputs } = {}) => {
    const live = liveRef.current;
    if (!live || !live.canConfigure || live.locked) {
      return {
        ok: false,
        error: {
          code: "COLLECTOR_EDITOR_LOCKED",
          message:
            "The collector editor is read-only right now (no configure permission, or a save is " +
            "in flight). Nothing was changed.",
        },
      };
    }
    const withScript =
      typeof script === "string" && script ? { ...live.form, script } : { ...live.form };
    const next = applyOutputSuggestions(withScript, outputs);
    setDirty(true);
    setSaveError("");
    setErrors((current) => {
      if (!current.script) return current;
      const rest = { ...current };
      delete rest.script;
      return rest;
    });
    setForm(next);
    return {
      ok: true,
      data: {
        applied: true,
        saved: false,
        script_lines: next.script.split("\n").length,
        suggested_outputs: next.outputs.filter((output) => output.suggested).map((o) => o.key),
        next_step:
          "The script is in the editor but NOT saved. Tell the operator to read it and click " +
          "Save, to accept or discard each suggested output, and — once saved — to press " +
          "Run now (dry run) before enabling the collector.",
      },
    };
  }, []);

  useEffect(
    () => registerCollectorEditor({ read: readForAssistant, apply: applyFromAssistant }),
    [readForAssistant, applyFromAssistant]
  );

  // The drawer is the gateway's own assistant: opening it here just raises it
  // with no prompt, so the operator types what the script should scrape. It
  // reads this editor live through the bridge above.
  const openAssistant = () => {
    if (typeof window === "undefined") return;
    try {
      window.dispatchEvent(
        new CustomEvent("gw-open-ai-with-prompt", {
          detail: { source: "custom-collector-script", collectorId: record?.id || "" },
        })
      );
    } catch {}
  };

  const patch = (changes) => {
    setDirty(true);
    setSaveError("");
    setForm((current) => ({ ...current, ...changes }));
  };

  const patchOutput = (uid, changes) => {
    setDirty(true);
    setSaveError("");
    setForm((current) => ({
      ...current,
      outputs: current.outputs.map((output) =>
        output.uid === uid ? { ...output, ...changes } : output
      ),
    }));
  };

  const addOutput = (preset) => {
    setDirty(true);
    setForm((current) => ({ ...current, outputs: [...current.outputs, newOutputForm(preset)] }));
  };

  const removeOutput = (uid) => {
    setDirty(true);
    setForm((current) => ({
      ...current,
      outputs: current.outputs.filter((output) => output.uid !== uid),
    }));
  };

  const acceptOutput = (uid) => patchOutput(uid, { suggested: false });

  const addOutputFromValue = (key, value) => {
    addOutput({
      key,
      type: inferOutputType(value),
      tagPath: suggestTagPath(form.name, key),
    });
  };

  const runDry = async () => {
    if (!record?.id) return;
    setRunning(true);
    setRunError("");
    try {
      setRunResult(await runCollectorNow(record.id, { dryRun: true }));
    } catch (error) {
      setRunResult(null);
      setRunError(errorMessage(error, "No se pudo correr el colector."));
    } finally {
      setRunning(false);
    }
  };

  const save = async () => {
    const validation = validateCollectorForm(form);
    setErrors(validation);
    if (Object.keys(validation).length > 0) {
      setSaveError("Revisa los campos marcados.");
      return;
    }
    setSaving(true);
    setSaveError("");
    try {
      const stored = record?.id
        ? await updateCollector(record.id, buildCollectorPayload(form))
        : await createCollector(buildCollectorPayload(form, { create: true }));
      setRecord(stored);
      setForm(collectorFormFromRecord(stored));
      setDirty(false);
      notify({
        type: "success",
        title: record?.id ? "Collector updated" : "Collector created",
        message: `"${stored.name}" ${
          record?.id ? "quedó guardado." : "quedó guardado y deshabilitado; habilítalo cuando el dry run se vea bien."
        }`,
      });
      onSaved?.(stored);
    } catch (error) {
      const message = errorMessage(error, "No se pudo guardar el colector.");
      const field = serverErrorField(message);
      // A field the server rejected is shown under that field; anything else
      // stays in the banner rather than being hidden under an unrelated input.
      if (field && field !== "outputs") setErrors((current) => ({ ...current, [field]: message }));
      setSaveError(message);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!record?.id) return;
    setDeleting(true);
    try {
      await deleteCollector(record.id);
      notify({
        type: "success",
        title: "Collector deleted",
        message: `"${record.name}" fue borrado. Los tags que creó siguen existiendo.`,
      });
      setConfirmingDelete(false);
      onDeleted?.(record);
      onClose();
    } catch (error) {
      const message = errorMessage(error, "No se pudo borrar el colector.");
      notify({ type: "error", title: "Delete failed", message });
      setConfirmingDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  const mappedKeys = form.outputs.map((output) => output.key.trim()).filter(Boolean);
  const dryRunKeys = runResult?.values ? Object.keys(runResult.values) : [];

  return (
    <>
      <GatewayModal
        title={saved ? `Edit ${record.name}` : "New collector"}
        subtitle="Un script de Python que el gateway corre cada cierto rato; sus values quedan en tags."
        width={980}
        className="gwCcModal"
        onClose={saving || deleting ? undefined : onClose}
        footer={
          <>
            {saved ? (
              <button
                type="button"
                className="gwDangerBtn gwCcDeleteBtn"
                disabled={locked}
                onClick={() => setConfirmingDelete(true)}
              >
                Delete
              </button>
            ) : null}
            <button type="button" className="gwGhostBtn" disabled={saving} onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="gwPrimaryBtn" disabled={locked} onClick={save}>
              {saving ? "Saving…" : "Save"}
            </button>
          </>
        }
      >
        {saveError ? (
          <div className="gwAlert" role="alert">
            <div className="gwAlertTitle">No se pudo guardar</div>
            <div className="gwAlertBody">{saveError}</div>
          </div>
        ) : null}

        <div className="gwCcGrid">
          <Field label="Name" error={errors.name}>
            <input
              className="gwInput"
              aria-label="Name"
              value={form.name}
              disabled={locked}
              placeholder="CMg Quillota 220"
              onChange={(event) => patch({ name: event.target.value })}
            />
          </Field>
          <Field
            label="Poll interval (s)"
            error={errors.pollSeconds}
            hint={`Mínimo ${MIN_POLL_INTERVAL_MS / 1000} s. Las corridas de un mismo colector nunca se solapan.`}
          >
            <NumberInput
              value={form.pollSeconds}
              min={MIN_POLL_INTERVAL_MS / 1000}
              aria-label="Poll interval (s)"
              disabled={locked}
              onChange={(event) => patch({ pollSeconds: event.target.value })}
            />
          </Field>
          <Field
            label="Timeout (s)"
            error={errors.timeoutSeconds}
            hint={`Entre ${MIN_TIMEOUT_MS / 1000} y ${MAX_TIMEOUT_MS / 1000} s. Al vencer, el proceso y sus hijos mueren.`}
          >
            <NumberInput
              value={form.timeoutSeconds}
              min={MIN_TIMEOUT_MS / 1000}
              max={MAX_TIMEOUT_MS / 1000}
              aria-label="Timeout (s)"
              disabled={locked}
              onChange={(event) => patch({ timeoutSeconds: event.target.value })}
            />
          </Field>
        </div>

        <Field label="Description">
          <input
            className="gwInput"
            aria-label="Description"
            value={form.description}
            disabled={locked}
            placeholder="Costo marginal desde el Coordinador"
            onChange={(event) => patch({ description: event.target.value })}
          />
        </Field>

        <div className="gwCcGrid gwCcGridHalf">
          <Field
            label="Params (JSON)"
            error={errors.params}
            hint="Llegan tal cual en el stdin del script. Son públicos: nunca pongas un secreto acá."
          >
            <textarea
              className="gwInput gwCcParams"
              aria-label="Params (JSON)"
              rows={5}
              spellCheck={false}
              value={form.params}
              disabled={locked}
              onChange={(event) => patch({ params: event.target.value })}
            />
          </Field>
          <EnvKeysField
            value={form.envKeys}
            error={errors.envKeys}
            disabled={locked}
            onChange={(envKeys) => patch({ envKeys })}
          />
        </div>

        <div className="gwCardSection">
          <div className="gwSectionTitle gwCcSectionHead">
            <span>Script</span>
            <div className="gwCcScriptHeadActions">
              <span className="gwCcScriptMeta">
                Python · {form.script.split("\n").length} líneas
              </span>
              <button
                type="button"
                className="gwSecondaryBtn gwCcAiBtn"
                disabled={locked}
                title={
                  canConfigure
                    ? "Describe en palabras qué debe leer el colector y la IA escribe el script"
                    : "Requires configure permission"
                }
                onClick={openAssistant}
              >
                <img src={sparksIcon} alt="" className="gwIcon gwCcAiIcon" />
                Generar con IA
              </button>
            </div>
          </div>
          <div className="gwCardSectionBody">
            <div className="gwHint">
              El script lee un objeto JSON de stdin (<code>params</code>, <code>now</code>,{" "}
              <code>previous</code>) e imprime un objeto JSON en stdout con{" "}
              <code>values</code>. Los diagnósticos van a stderr. Solo hay biblioteca
              estándar: corre con <code>python -I</code>, sin site-packages.
            </div>
            {errors.script ? <div className="gwCcFieldError">{errors.script}</div> : null}
            <ScriptEditor
              value={form.script}
              disabled={locked}
              onChange={(script) => patch({ script })}
            />
          </div>
        </div>

        <DryRunPanel
          result={runResult}
          error={runError}
          running={running}
          dirty={dirty}
          saved={saved}
          mappedKeys={mappedKeys}
          onRun={runDry}
          onAddOutput={addOutputFromValue}
        />

        <OutputsTable
          outputs={form.outputs}
          errors={errors.outputs}
          disabled={locked}
          valueKeys={dryRunKeys}
          onChange={patchOutput}
          onRemove={removeOutput}
          onAccept={acceptOutput}
          onAdd={() => addOutput({})}
        />

        {saved ? <RunHistory collectorId={record.id} /> : null}
      </GatewayModal>
      {confirmingDelete ? (
        <GatewayConfirmDialog
          title="Delete collector"
          body={`Se borra "${record.name}" y su historial de corridas. Los tags que creó no se borran: pueden llevar meses de tendencia.`}
          confirmLabel={deleting ? "Deleting…" : "Delete"}
          danger
          busy={deleting}
          zIndex={120}
          onCancel={() => setConfirmingDelete(false)}
          onConfirm={confirmDelete}
        />
      ) : null}
    </>
  );
}

export default CustomCollectorEditor;
